import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";

const LOGO_URL = "/logo.png";

const NAV_ITEMS = [
  { label: "Accueil", path: "/" },
  { label: "Notre Mission", path: "/mission" },
  { label: "Programmes", path: "/programmes" },
  { label: "Projets", path: "/projets" },
  { label: "Blog", path: "/blog" },
  { label: "Partenaires", path: "/partenaires" },
  { label: "B\u00e9n\u00e9voles", path: "/benevoles" },
  { label: "Contact", path: "/contact" },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-slate-950/90 backdrop-blur-xl border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="h-11 w-11 rounded-xl bg-slate-800/80 ring-1 ring-blue-500/30 p-0.5 flex items-center justify-center overflow-hidden">
            <img src={LOGO_URL} alt="EDUCPEOPLE" className="h-full w-full object-contain" style={{ filter: 'brightness(1.8) saturate(1.4)' }} />
          </div>
          <span className="font-heading text-xl md:text-2xl font-semibold text-slate-50 tracking-tight">
            EDUCPEOPLE
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace("/", "") || "home"}`}
              className={`font-body text-sm tracking-wide transition-colors duration-200 link-underline ${
                location.pathname === item.path
                  ? "text-blue-400"
                  : "text-slate-300 hover:text-slate-50"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/soutenir"
            data-testid="header-donate-btn"
            className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-6 py-2.5 rounded-full font-body text-sm font-medium transition-all duration-300"
          >
            <Heart className="w-4 h-4" />
            Soutenir
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-slate-50 p-2"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {isOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-t border-white/10 animate-fade-in"
        >
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.path.replace("/", "") || "home"}`}
                className={`font-body text-base py-2 transition-colors ${
                  location.pathname === item.path
                    ? "text-blue-400"
                    : "text-slate-300 hover:text-slate-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/soutenir"
              data-testid="mobile-donate-btn"
              className="inline-flex items-center justify-center gap-2 bg-blue-500 text-white px-6 py-3 rounded-full font-body text-sm font-medium mt-2"
            >
              <Heart className="w-4 h-4" />
              Soutenir l'association
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
